import {
  createEntityAdapter,
  createSlice,
  isAnyOf,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';

import {
  MessageType,
  type ListMessagesRequest,
  type PagingMetadataV2,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import { senderIsOwner } from '@/utils/sender';

import {
  getMessagesHistory,
  getEditorMockMessages,
  getConversationId,
} from './messagesThunks';
import { Message } from './model';

const MANUAL_INTERVENTION_MAX_AGE_MS = 10 * 60 * 1000;

export const messagesAdapter = createEntityAdapter<Message>({
  sortComparer: (a, b) => {
    if (a.sortKey && b.sortKey) {
      return (a.sortKey ?? '').localeCompare(b.sortKey ?? '');
    }

    if (a.createdDate && b.createdDate) {
      return a.createdDate - b.createdDate;
    }
    return 0;
  },
});

const initialState = messagesAdapter.getInitialState<{
  request: 'idle' | 'loading' | 'loadingHistory' | 'error';
  conversationIdRequest: 'idle' | 'loading';
  pendingMessages: number;
  currentRequestId?: string;
  isTyping: boolean;
  awaitingAnswer: boolean;
  manualInterventionActive: boolean;
  manualInterventionPending: boolean;
  hasUnread: boolean;
  firstUnreadMessageId: string | null;
  pagingMetadata: PagingMetadataV2 | null;
  conversationId: string | null;
  historyContactFormIds: string[];
}>({
  request: 'idle',
  conversationIdRequest: 'idle',
  pendingMessages: 0,
  isTyping: false,
  awaitingAnswer: false,
  manualInterventionActive: false,
  manualInterventionPending: false,
  hasUnread: false,
  firstUnreadMessageId: null,
  pagingMetadata: null,
  conversationId: null,
  historyContactFormIds: [],
});

const localAdapterSelectors = messagesAdapter.getSelectors();

function hasRecentOwnerMessage(messages: Message[]) {
  const recentThreshold = Date.now() - MANUAL_INTERVENTION_MAX_AGE_MS;

  return messages.some(
    (message) =>
      senderIsOwner(message.sender) &&
      Boolean(message.createdDate && message.createdDate >= recentThreshold),
  );
}

function syncManualInterventionFromHistory(state: typeof initialState) {
  state.manualInterventionActive = hasRecentOwnerMessage(
    localAdapterSelectors.selectAll(state),
  );
  state.manualInterventionPending = false;
}

function addHistoryContactFormIds(
  state: typeof initialState,
  messages: Message[],
) {
  const contactFormIds = messages
    .filter((message) => message.messageType === MessageType.CONTACT_FORM)
    .map((message) => message.id);

  if (!contactFormIds.length) {
    return;
  }

  state.historyContactFormIds = Array.from(
    new Set([...(state.historyContactFormIds ?? []), ...contactFormIds]),
  );
}

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    replace: messagesAdapter.setAll,
    addMessage: messagesAdapter.addOne,
    addOptimisticMessage: (state, { payload }: PayloadAction<Message>) => {
      state.pendingMessages++;
      messagesAdapter.addOne(state, payload);
    },
    resetConversation: (state, { payload }: PayloadAction<string>) => {
      state.pendingMessages = 0;
      state.request = 'idle';
      state.currentRequestId = undefined;
      state.isTyping = false;
      state.awaitingAnswer = false;
      state.manualInterventionActive = false;
      state.manualInterventionPending = false;
      state.hasUnread = false;
      state.firstUnreadMessageId = null;
      state.pagingMetadata = null;
      state.conversationId = payload;
      state.historyContactFormIds = [];
    },
    resetMessages: (
      state,
      {
        payload: { messages, pagingMetadata },
      }: PayloadAction<{
        pagingMetadata?: PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.setAll(state, messages);
      state.firstUnreadMessageId = null;
      state.pagingMetadata = pagingMetadata ?? null;
      state.historyContactFormIds = [];
      syncManualInterventionFromHistory(state);
    },
    addMessages: (
      state,
      {
        payload,
      }: PayloadAction<{
        pagingMetadata?: PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.addMany(state, payload.messages);
      state.pagingMetadata = payload.pagingMetadata!;
      addHistoryContactFormIds(state, payload.messages);
      syncManualInterventionFromHistory(state);
    },
    updateMessage: (
      state,
      { payload }: PayloadAction<Update<Message, string>>,
    ) => {
      // Message has got an id means it's no longer an optimistic
      if (
        payload.id &&
        payload.changes.id &&
        payload.id !== payload.changes.id &&
        state.pendingMessages > 0 // In case someone uses addMessage instead of addOptimisticMessage
      ) {
        state.pendingMessages--;
      }
      messagesAdapter.updateOne(state, payload);
    },
    appendMessageText: (
      state,
      { payload: { id, text } }: PayloadAction<{ id: string; text: string }>,
    ) => {
      const target = state.entities[id];
      if (!target) {
        return;
      }
      const body = (target.body = target.body || {});
      body.mainText = (body.mainText ?? '') + text;
    },
    addOrUpdateMessage: messagesAdapter.upsertOne,
    removeMessage: messagesAdapter.removeOne,
    setConversationId: (state, { payload }: PayloadAction<string>) => {
      state.conversationId = payload;
    },
    setIsTyping: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.isTyping = !!payload;
    },
    setAwaitingAnswer: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.awaitingAnswer = !!payload;
    },
    setManualInterventionActive: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionActive = !!payload;
    },
    setManualInterventionPending: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionPending = !!payload;
    },
    setUnread: (state, { payload }: PayloadAction<boolean>) => {
      state.hasUnread = payload;
    },
    markUnreadFromMessage: (state, { payload }: PayloadAction<string>) => {
      state.hasUnread = true;
      state.firstUnreadMessageId ??= payload;
    },
    clearFirstUnreadMessageId: (state) => {
      state.firstUnreadMessageId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConversationId.pending, (state) => {
      state.conversationIdRequest = 'loading';
    });
    builder.addCase(getConversationId.fulfilled, (state) => {
      state.conversationIdRequest = 'idle';
    });
    builder.addMatcher(
      isAnyOf(getMessagesHistory.pending, getEditorMockMessages.pending),
      (state, action) => {
        const isHistoryRequest = Boolean(
          (action.meta.arg as ListMessagesRequest)?.cursorPaging?.cursor,
        );
        state.currentRequestId = action.meta.requestId;
        state.request = isHistoryRequest ? 'loadingHistory' : 'loading';
      },
    );
    builder.addMatcher(
      isAnyOf(getMessagesHistory.fulfilled, getEditorMockMessages.fulfilled),
      (state, action) => {
        if (state.currentRequestId === action.meta.requestId) {
          state.currentRequestId = undefined;
          state.request = 'idle';
        }
      },
    );
  },
});

export const { reducer: messagesReducer } = messagesSlice;
